import { IconName } from "@/services/icons";
import { ResourceChannel } from "@/services/resources";
import { Organization } from "@/services/organization";
import {
  formatCurrency,
  formatCurrencyCompact,
} from "@/utils/filters/currencies";
import { formatDuration } from "@/utils/filters/duration";
import { formatNumber, formatNumberCompact } from "@/utils/filters/numbers";
import { assertUnreachable } from "@/utils/typing";
import {
  CalculatedMetricKey,
  DynamicMetricDetails,
} from "@/services/calculated_metrics";
import { upperFirst } from "lodash-es";
import type { Analytics4Metric } from "@/services/website_performance_ga4";
import type { MatomoMetric } from "@/services/website_performance_matomo";

export enum MetricFormat {
  MONETARY = "monetary",
  RELATIVE = "relative",
  INTEGER = "integer",
  DECIMAL = "decimal",
  DURATION = "duration",
  STATE = "state",
}

export enum MetricFilterRelationships {
  EQUAL_TO = "equal to",
  LESS_THAN = "less than",
  LESS_THAN_OR_EQUAL_TO = "less than or equal to",
  GREATER_THAN_OR_EQUAL_TO = "greater than or equal to",
  GREATER_THAN = "greater than",
  NOT_EQUAL_TO = "not equal to",
}

export interface MetricDetail {
  key: MetricKey;
  longName: string;
  shortName: string;
  description: string;
  formatType: MetricFormat;
  icon?: IconName;
  summable: boolean;
  aggregatable: boolean;
  invertedPositive: boolean; // for cost metrics
  invertedDirection: boolean;
}

type OptionalMetricAttribute =
  | "longName"
  | "summable"
  | "invertedPositive"
  | "invertedDirection"
  | "description"
  | "aggregatable";
type MetricDetailWithOptionalAttributes = Omit<
  MetricDetail,
  "key" | OptionalMetricAttribute
> &
  Partial<Pick<MetricDetail, OptionalMetricAttribute>>;

const _metricDetails = {
  addToCarts: {
    formatType: MetricFormat.INTEGER,
    shortName: "Add-to-carts",
    icon: "shopping-cart",
  },
  checkouts: {
    formatType: MetricFormat.INTEGER,
    shortName: "Checkouts",
    icon: "arrow-alt-from-left",
  },
  purchases: {
    formatType: MetricFormat.INTEGER,
    shortName: "Purchases",
    icon: "money-bill-wave",
  },
  clicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Clicks",
    icon: "mouse-pointer",
    description:
      "The number of clicks on links within the ad that led to advertiser-specified destinations, on or off the third party channel.",
  },
  totalClicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Clicks (All)",
    icon: "mouse-pointer",
  },
  impressions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Impressions",
    icon: "eye",
  },
  shares: {
    formatType: MetricFormat.INTEGER,
    shortName: "Shares",
    icon: "link",
  },
  comments: {
    formatType: MetricFormat.INTEGER,
    shortName: "Comments",
    icon: "comment-dots",
  },
  reactions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Reactions",
    icon: "thumbs-up",
    description: 'The count of "reacts" (like, love, sad, etc.) on a post.',
  },
  sessions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Sessions",
    icon: "hourglass-half",
  },
  engagedSessions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Engaged sessions",
    description:
      "Sessions that had a conversion event, had 2 or more page views, \n or lasted at least 10 seconds (you can define up to \n 60 seconds in Google Analytics 4).",
    icon: "heartbeat-solid",
  },
  users: {
    formatType: MetricFormat.INTEGER,
    shortName: "Total users",
    icon: "users",
    summable: false,
  },
  newUsers: {
    formatType: MetricFormat.INTEGER,
    shortName: "New users",
    icon: "user-plus",
    description: "Users who visited your site for the first time.",
  },
  followers: {
    formatType: MetricFormat.INTEGER,
    shortName: "Followers",
    icon: "users",
    summable: false,
  },
  followersGrowth: {
    formatType: MetricFormat.INTEGER,
    shortName: "Followers Growth",
    icon: "users",
  },
  pageViews: {
    formatType: MetricFormat.INTEGER,
    shortName: "Page views",
    icon: "eye",
  },
  pageViewsPerUser: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Views/user",
    longName: "Page views per total users",
    icon: "eye",
    summable: false,
  },
  pageViewsPerActiveUser: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Views/active user",
    longName: "Page views per active users",
    icon: "eye",
    summable: false,
  },
  posts: {
    formatType: MetricFormat.INTEGER,
    shortName: "Posts",
  },
  pagesPerSession: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Pages/session",
    longName: "Pages per session",
    summable: false,
  },
  engagement: {
    formatType: MetricFormat.INTEGER,
    shortName: "Engagement",
    icon: "heartbeat-solid",
    description:
      "Engangement is the sum of actions performed on your organic post or ad. \n Depending on platform, it can for example be the sum of reactions, shares, \n comments, video plays, page likes and link clicks.",
  },
  transactions: {
    formatType: MetricFormat.INTEGER,
    icon: "handshake",
    shortName: "Transactions",
  },
  lighthouseScore: {
    formatType: MetricFormat.INTEGER,
    shortName: "Lighthouse score",
  },
  qualityScore: {
    formatType: MetricFormat.INTEGER,
    shortName: "Quality score",
    icon: "certificate-solid",
    description:
      "An estimate of the relevance of your ads, keywords and landing pages to people seeing your ads.",
  },
  conversions: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Conversions",
    description: "Sourced from the connected platform.",
    icon: "sync-alt",
  },
  conversionsByConvDate: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Conv. (conv. date)",
    longName: "Conversions (by conv. date)",
    description: "Only available for Google Ads",
    icon: "sync-alt",
  },
  conversionValue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Conversion value",
    icon: "money-bill-wave",
  },
  conversionValueByConvDate: {
    formatType: MetricFormat.MONETARY,
    shortName: "Conv. val(conv.date)",
    longName: "Conversion value (by conv. date)",
    icon: "money-bill-wave",
  },
  totalConversions: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Total conversions",
  },
  eventCount: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Event count",
    longName: "Conversions and event count",
    icon: "sync-alt",
  },
  avgPosition: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Avg. position",
    longName: "Average position",
    description:
      "The average position in the Google Search Engine Results Page \n (SERP), 1 being the highest",
    summable: false,
    invertedPositive: true,
    invertedDirection: true,
    icon: "list-ol",
  },
  averagePosition: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Avg. position",
    longName: "Average position",
    description:
      "The average position in the Google Search Engine Results Page \n (SERP), 1 being the highest",
    summable: false,
    invertedPositive: true,
    invertedDirection: true,
    icon: "list-ol",
  },
  postsPerDay: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Posts/day",
    longName: "Posts per day",
    summable: false,
  },
  averageMonthlySearches: {
    formatType: MetricFormat.INTEGER,
    shortName: "Searches/month",
    longName: "Searches per month",
  },
  pageViewsPerSession: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Views/sess.",
    longName: "Page views per session",
    description:
      "The number of app screens or web pages your users viewed per session. Repeated views of a single page or screen are counted. (screen_view + page_view events) / sessions.",
    summable: false,
    icon: "eye",
  },
  conversionsPerImpression: {
    formatType: MetricFormat.DECIMAL,
    shortName: "CPI",
    longName: "Conversions per impression",
    description: "Conversions per impression.",
    summable: false,
  },
  purchaseRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Purchase revenue",
    icon: "money-bill-wave",
  },
  value: {
    formatType: MetricFormat.MONETARY,
    shortName: "Conversion value",
    icon: "money-bill-wave",
  },
  valueByConvDate: {
    formatType: MetricFormat.MONETARY,
    shortName: "Conv. val(conv.date)",
    longName: "Conversion value (by conv. date)",
    icon: "money-bill-wave",
  },
  budget: {
    formatType: MetricFormat.MONETARY,
    icon: "coins",
    shortName: "Budget",
  },
  biddingStrategy: {
    formatType: MetricFormat.STATE,
    shortName: "Bid strat.",
    longName: "Bidding strategy",
    icon: "coins",
  },
  status: {
    formatType: MetricFormat.STATE,
    shortName: "Status",
    icon: "check-circle",
    description:
      "Whether the ad network reports this entity as actively serving, paused, blocked by issues, ended or archived. " +
      "This is the current status as of the last data sync — unlike metrics, it is not historical " +
      "and does not change with the selected date range.",
  },
  cost: {
    formatType: MetricFormat.MONETARY,
    icon: "coins",
    shortName: "Cost",
    description:
      "The total cost of running an account, campaign, ad or similar.",
    invertedPositive: true,
  },
  costPerClick: {
    formatType: MetricFormat.MONETARY,
    shortName: "CPC",
    longName: "Cost per click",
    description: "The total cost per click an ad recieves.",
    summable: false,
    invertedPositive: true,
    icon: "coins",
  },
  costPerAction: {
    formatType: MetricFormat.MONETARY,
    shortName: "CPA",
    longName: "Cost per action",
    description:
      "Cost per action, or cost per acquisition (CPA) \n measures how much it costs to generate an action.",
    summable: false,
    invertedPositive: true,
    icon: "coins",
  },
  costPerConversion: {
    formatType: MetricFormat.MONETARY,
    shortName: "Cost/conversion",
    longName: "Cost per conversion",
    summable: false,
    invertedPositive: true,
    icon: "coins",
  },
  costPerMilli: {
    formatType: MetricFormat.MONETARY,
    shortName: "CPM",
    longName: "Cost per 1000 impressions",
    description: "Cost per mille (CPM) is the cost per 1000 impressions.",
    summable: false,
    invertedPositive: true,
    icon: "coins",
  },
  returnOnAdSpend: {
    formatType: MetricFormat.RELATIVE,
    shortName: "ROAS%",
    longName: "Return on ad spend (percentage)",
    description:
      "Return on ad spend measures how much revenue you receive compared to how much you spend on advertising. It is calculated as value divided by cost, multiplied by 100.",
    summable: false,
    icon: "sack-dollar",
  },
  returnOnAdSpendRatio: {
    formatType: MetricFormat.DECIMAL,
    shortName: "ROAS",
    longName: "Return on ad spend (ratio)",
    description:
      "Return on ad spend measures how much revenue you receive compared to how much you spend on advertising. It is calculated as value divided by cost.",
    summable: false,
    icon: "sack-dollar",
  },
  returnOnAdSpendByConvDate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "ROAS% (conv. date)",
    longName: "Return on ad spend (percentage) by conv. date",
    description:
      "Return on ad spend measures how much revenue you receive compared to how much you spend on advertising. It is calculated as value of conversions by conversion date divided by cost, multiplied by 100.",
    summable: false,
    icon: "sack-dollar",
  },
  returnOnAdSpendRatioByConvDate: {
    formatType: MetricFormat.DECIMAL,
    shortName: "ROAS (conv. date)",
    longName: "Return on ad spend (ratio) by conv. date",
    description:
      "Return on ad spend measures how much revenue you receive compared to how much you spend on advertising. It is calculated as value of conversions by conversion date divided by cost.",
    summable: false,
    icon: "sack-dollar",
  },
  revenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Revenue",
  },
  averageOrderValue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Avg. order value",
    longName: "Average order value",
    summable: false,
  },
  maxCostPerClick: {
    formatType: MetricFormat.MONETARY,
    shortName: "Max CPC",
    longName: "Max cost per click",
    description: "The max amount you will pay for an ad click.",
  },
  estimatedFirstPositionBid: {
    formatType: MetricFormat.MONETARY,
    shortName: "Est. 1st pos. bid",
    longName: "Estimated first position bid",
    description:
      "An estimate of the amount to bid for your ad to be shown first in Google search results.",
    summable: false,
    invertedPositive: true,
  },
  avgBounceRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Avg. bounce rate",
    longName: "Average bounce rate",
    description:
      "Bounce rate is the percentage of visitors that leave a webpage while not having performed an action on the page.",
    summable: false,
    invertedPositive: true,
  },
  engagementRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Eng. rate",
    longName: "Engagement rate",
    description:
      "In SoMe, this metric measures engagement (number of actions on a post or ad) divided by number of impressions. \n In Google Analytics 4, it measures engaged sessions divided by sessions",
    summable: false,
    icon: "heartbeat-solid",
  },
  likes: {
    formatType: MetricFormat.INTEGER,
    shortName: "Likes",
    icon: "thumbs-up",
  },
  clickthroughRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "CTR",
    longName: "Clickthrough rate",
    description:
      "Clickthrough rate (CTR) is the number of clicks divided by the number of impressions.",
    summable: false,
    icon: "hand-pointer",
  },
  conversionRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Conversion rate",
    summable: false,
  },
  clickRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Click rate",
    icon: "hand-pointer",
    summable: false,
  },
  bounceRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Bounce rate",
    summable: false,
    invertedPositive: true,
    icon: "arrow-alt-to-left",
  },
  followerGrowth: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Follower growth",
    icon: "user-plus",
    summable: false,
  },
  avgTimeOnPage: {
    formatType: MetricFormat.DURATION,
    shortName: "Avg. time on page",
    longName: "Average time on page",
    summable: false,
    icon: "stopwatch",
  },
  avgSessionDuration: {
    formatType: MetricFormat.DURATION,
    shortName: "Avg. sess. duration",
    longName: "Average session duration",
    summable: false,
    icon: "stopwatch",
  },
  engagementTimePerSession: {
    formatType: MetricFormat.DURATION,
    shortName: "Eng. time/session",
    longName: "Engagement time per session",
    summable: false,
    icon: "stopwatch",
  },
  engagementTimePerPage: {
    formatType: MetricFormat.DURATION,
    shortName: "Engagement time/page",
    longName: "Engagement time per page",
    summable: false,
    icon: "stopwatch",
  },
  uniqueVisitors: {
    formatType: MetricFormat.INTEGER,
    shortName: "Unique visitors",
    summable: false,
    icon: "user",
  },
  actionsPerVisit: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Actions/visit",
    longName: "Actions per visit",
    summable: false,
    icon: "mouse-pointer",
  },
  maxActions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Max actions",
    longName: "Max actions in a visit",
    summable: false,
    icon: "arrow-up",
  },
  competition: {
    formatType: MetricFormat.STATE,
    shortName: "Competition",
    icon: "award-solid",
  },
  expectedClickthroughRate: {
    formatType: MetricFormat.STATE,
    shortName: "Expected CTR",
    longName: "Expected clickthrough rate",
    icon: "hand-pointer",
    description:
      "An estimated measure of the likelihood of people clicking your ad.",
  },
  landingPageExperience: {
    formatType: MetricFormat.STATE,
    icon: "browser",
    shortName: "Landing page exp.",
    longName: "Landing page experience",
    description:
      "An estimate of the usefulness of your landing page to users clicking on your ad.",
  },
  quantity: {
    formatType: MetricFormat.INTEGER,
    shortName: "Quantity",
  },
  averagePrice: {
    formatType: MetricFormat.MONETARY,
    shortName: "Average Price",
    summable: false,
  },
  postsPublished: {
    formatType: MetricFormat.INTEGER,
    shortName: "Posts Published",
  },
  adsCount: {
    formatType: MetricFormat.INTEGER,
    shortName: "Active Ads",
    longName: "Number of active ads",
    description:
      "Number of distinct ads with more than 0 impressions in the selected period.",
    icon: "eye",
    // Not summable (you can't add two periods' counts), but aggregatable across
    // resources: the backend counts distinct ads over all selected accounts
    // combined, and ad PKs are unique per account so there's no double-counting.
    summable: false,
    aggregatable: true,
  },
  reach: {
    formatType: MetricFormat.INTEGER,
    shortName: "Reach",
    icon: "broadcast-tower",
    description:
      "The number of unique accounts that saw your content at least once. Not additive — totals across posts or periods overcount.",
    summable: false,
    aggregatable: false,
  },
  saves: {
    formatType: MetricFormat.INTEGER,
    shortName: "Saves",
    icon: "star-solid",
    description:
      "The number of times this post was saved by users (Instagram).",
  },
  linkClicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Link clicks",
    icon: "mouse-pointer",
    description:
      "Clicks on links in the post that led off-platform. A subset of Clicks, which counts all post clicks (Facebook).",
  },
  videoWatchTime: {
    formatType: MetricFormat.DURATION,
    shortName: "Watch time",
    icon: "stopwatch",
    description:
      "Total time spent watching this video across all views (TikTok).",
    // Stored as a per-row lifetime reading, so summing across posts/periods
    // double-counts — non-additive, like reach.
    summable: false,
    aggregatable: false,
  },
  // Story metrics (Instagram/Facebook stories). These are count-style values
  // (replies, taps, exits, follows, …), so they sum cleanly across a period AND
  // across accounts — summable + aggregatable, like impressions/clicks. (Story
  // REACH is the exception: it reuses the shared `reach` entry, which stays
  // aggregatable:false because unique-account reach can't be summed.)
  storyReplies: {
    formatType: MetricFormat.INTEGER,
    shortName: "Replies",
    icon: "comment-dots",
    description: "Direct-message replies to this story.",
    summable: true,
    aggregatable: true,
  },
  storyInteractions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Interactions",
    icon: "heartbeat-solid",
    description:
      "Total interactions on this story per the platform (replies, sticker taps, etc.).",
    summable: true,
    aggregatable: true,
  },
  storyFollows: {
    formatType: MetricFormat.INTEGER,
    shortName: "Follows",
    icon: "user-plus",
    description: "Accounts that started following from this story.",
    summable: true,
    aggregatable: true,
  },
  storyProfileVisits: {
    formatType: MetricFormat.INTEGER,
    shortName: "Profile visits",
    icon: "users",
    description: "Profile visits driven by this story.",
    summable: true,
    aggregatable: true,
  },
  storyTapsForward: {
    formatType: MetricFormat.INTEGER,
    shortName: "Taps forward",
    icon: "arrow-circle-right",
    description: "Taps to skip to the next story.",
    summable: true,
    aggregatable: true,
  },
  storyTapsBack: {
    formatType: MetricFormat.INTEGER,
    shortName: "Taps back",
    icon: "arrow-left",
    description: "Taps to return to the previous story.",
    summable: true,
    aggregatable: true,
  },
  storyExits: {
    formatType: MetricFormat.INTEGER,
    shortName: "Exits",
    icon: "arrow-up-right-from-square",
    description: "Times viewers exited out of the story feed.",
    summable: true,
    aggregatable: true,
  },
  storySwipeForward: {
    formatType: MetricFormat.INTEGER,
    shortName: "Swipe forward",
    icon: "long-arrow-right",
    description: "Swipes forward to the next account's story.",
    summable: true,
    aggregatable: true,
  },
  frequency: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Frequency",
    icon: "signal-stream",
    description: "The average number of times each person saw your ad.",
    summable: false,
    aggregatable: false,
  },
  initialVideoViews: {
    formatType: MetricFormat.INTEGER,
    shortName: "Initial video views",
    description: `2 or 3 second video view, depending on the channel.
    2 seconds for Snapchat, TikTok, LinkedIn and Microsoft Ads, 3 seconds for Meta. LinkedIn also counts call-to-action clicks. Other channels do not have this metric.`,
    icon: "video",
  },
  videoViews25Pct: {
    formatType: MetricFormat.INTEGER,
    shortName: "25% video views",
    description: "The number of times at least 25% of the video was watched.",
    icon: "video",
  },
  videoViews50Pct: {
    formatType: MetricFormat.INTEGER,
    shortName: "50% video views",
    description: "The number of times at least 50% of the video was watched.",
    icon: "video",
  },
  videoViews75Pct: {
    formatType: MetricFormat.INTEGER,
    shortName: "75% video views",
    description: "The number of times at least 75% of the video was watched.",
    icon: "video",
  },
  videoViews100Pct: {
    formatType: MetricFormat.INTEGER,
    shortName: "Video completions",
    description: "The number of times the entire video was watched.",
    icon: "video",
  },
  metaThruplays: {
    formatType: MetricFormat.INTEGER,
    shortName: "Thruplays",
    description:
      "Meta Ads specific. For videos shorter than 15 seconds, thruplays are counted when the video is played to completion. Longer videos must be played for at least 15 seconds.",
    icon: "video",
  },
  youtubeViews: {
    formatType: MetricFormat.INTEGER,
    shortName: "Youtube views",
    description: `Google Ads specific. This is counted differently based on the type of video ad.

      For skippable in-stream ads, it is a 30-second watch (or until the end if it's shorter). For in-feed video ads, the user must click the thumbnail or watch at least 10 seconds (or until the end if it's shorter). For Shorts ads, users must click the call-to-action button or watch at least 10 seconds (or until the end if shorter).`,
    icon: "video",
  },
  averageVideoWatchTime: {
    formatType: MetricFormat.DURATION,
    shortName: "Avg. video time",
    longName: "Average video watch time",
    description: "The average duration the video was watched per play.",
    icon: "video",
    summable: false,
  },
  costPerInitialVideoView: {
    formatType: MetricFormat.MONETARY,
    shortName: "Cost/initial view",
    longName: "Cost per initial video view",
    description:
      "Calculated as total cost divided by initial video views. Note that this will include cost for ads not eligible for video views.",
    icon: "coins",
    summable: false,
  },
  costPerMetaThruplay: {
    formatType: MetricFormat.MONETARY,
    shortName: "Cost/thruplay",
    longName: "Cost per thruplay",
    description:
      "Calculated as total cost divided by thruplays. Note that this will include cost for ads not eligible for thruplays.",
    icon: "coins",
    summable: false,
  },
  youtubeViewRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Youtube view rate",
    description:
      "Google Ads specific. Youtube views divided by the number of impressions on ads that are eligible to receive views.",
    icon: "video",
    summable: false,
  },
  activeUsers: {
    formatType: MetricFormat.INTEGER,
    shortName: "Active users",
    description: "The number of distinct users who visited your site or app.",
    summable: false,
    icon: "users",
  },
  adUnitExposure: {
    formatType: MetricFormat.INTEGER,
    shortName: "Ad unit exposure",
    description:
      "The time that an ad unit was exposed to a user, in milliseconds.",
    icon: "eye",
  },
  adsClicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Ads clicks",
    description:
      "Total number of times users have clicked on an ad to reach the property. Includes clicks from linked integrations like linked Search Ads 360 advertisers. Also includes uploaded clicks from data import.",
    icon: "mouse-pointer",
  },
  adsCost: {
    formatType: MetricFormat.MONETARY,
    shortName: "Ads cost",
    description:
      "The total amount you paid for your ads. Includes costs from linked integrations like linked Google Ads accounts. Also includes uploaded cost from data import.",
  },
  adsCostPerClick: {
    formatType: MetricFormat.MONETARY,
    shortName: "Ads CPC",
    longName: "Ads cost per click",
    description:
      "Ads cost per click is ad cost divided by ad clicks and is often abbreviated CPC.",
    summable: false,
    icon: "coins",
  },
  adsImpressions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Ads impressions",
    description:
      "The total number of impressions. Includes impressions from linked integrations like linked Display & Video 360 advertisers. Also includes uploaded impressions from data import.",
  },
  averagePurchaseRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Avg. purch. revenue",
    longName: "Average purchase revenue",
    description:
      "The average purchase revenue in the transaction group of events.",
    summable: false,
    icon: "money-bill-wave",
  },
  averagePurchaseRevenuePerPayingUser: {
    formatType: MetricFormat.MONETARY,
    shortName: "ARPPU",
    longName: "Average revenue per paying user",
    description:
      "Average revenue per paying user (ARPPU) is the total purchase revenue per active user that logged a purchase event.",
    summable: false,
    icon: "money-bill-wave",
  },
  averagePurchaseRevenuePerUser: {
    formatType: MetricFormat.MONETARY,
    shortName: "Avg. purch. rev/user",
    longName: "Average purchase revenue per user",
    description:
      "The average purchase revenue per active user is the total purchase revenue per active user that logged any event.",
    summable: false,
    icon: "money-bill-wave",
  },
  averageRevenuePerUser: {
    formatType: MetricFormat.MONETARY,
    shortName: "ARPU",
    longName: "Average revenue per user",
    description:
      "Average revenue per active user (ARPU). ARPU uses Total Revenue and includes AdMob estimated earnings.",
    summable: false,
    icon: "money-bill-wave",
  },
  averageSessionDuration: {
    formatType: MetricFormat.DURATION,
    shortName: "Avg. sess. duration",
    longName: "Average session duration",
    description: "The average duration of users' sessions.",
    summable: false,
    icon: "stopwatch",
  },
  cartToViewRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Cart-to-view rate",
    description:
      "The number of users who added a product(s) to their cart divided by the number of users who viewed the same product(s).",
    summable: false,
    icon: "shopping-cart",
  },
  crashAffectedUsers: {
    formatType: MetricFormat.INTEGER,
    shortName: "Crash-affected users",
    description:
      'The number of users that logged a crash in this row of the report. For example if the report is time series by date, this metrics reports total users with at least one crash on this date. Crashes are events with the name "app_exception".',
    icon: "user-injured",
  },
  crashFreeUsersRate: {
    formatType: MetricFormat.INTEGER,
    shortName: "Crash-free rate",
    longName: "Crash-free users rate",
    description:
      "The number of users without crash events divided by the total number of users.",
    summable: false,
    icon: "user",
  },
  dauPerMau: {
    formatType: MetricFormat.RELATIVE,
    shortName: "DAU / MAU",
    description:
      "The rolling percent of 30-day active users who are also 1-day active users.",
    summable: false,
    icon: "users",
  },
  dauPerWau: {
    formatType: MetricFormat.RELATIVE,
    shortName: "DAU / WAU",
    description:
      "The rolling percent of 7-day active users who are also 1-day active users.",
    summable: false,
    icon: "users",
  },
  eventCountPerUser: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Event count per user",
    description:
      "The average number of events per user (Event count divided by Active users).",
    summable: false,
  },
  eventsPerSession: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Events per session",
    description:
      "The average number of events per session (Event count divided by Sessions).",
    summable: false,
  },
  firstTimePurchaserRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "1st purchase rate",
    longName: "First-time purchaser rate",
    description:
      "The percentage of active users who made their first purchase.",
    summable: false,
  },
  firstTimePurchasers: {
    formatType: MetricFormat.INTEGER,
    shortName: "1st-time purchasers",
    longName: "First-time purchasers",
    description:
      "The number of users that completed their first purchase event.",
  },
  firstTimePurchasersPerNewUser: {
    formatType: MetricFormat.DECIMAL,
    shortName: "1st purchasers/user",
    longName: "First-time purchasers per new user",
    description: "The average number of first time purchasers per new user.",
  },
  grossItemRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Gross item revenue",
    description:
      "The total revenue from items only. Gross item revenue is the product of its price and quantity. Item revenue excludes tax and shipping values; tax & shipping values are specified at the event and not item level. Gross item revenue does not include refunds.",
    icon: "money-bill-wave",
  },
  grossPurchaseRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Gross purch. rev.",
    longName: "Gross purchase revenue",
    description:
      "The sum of revenue from purchases made in your app or site. Gross purchase revenue sums the revenue for these events: purchase, ecommerce_purchase, in_app_purchase, app_store_subscription_convert, and app_store_subscription_renew. Purchase revenue is specified by the value parameter in tagging.",
    icon: "money-bill-wave",
  },
  itemDiscountAmount: {
    formatType: MetricFormat.MONETARY,
    shortName: "Item discount amount",
    description:
      "The monetary value of item discounts in eCommerce events. This metric is populated in tagging by the discount item parameter.",
  },
  itemListClickEvents: {
    formatType: MetricFormat.INTEGER,
    shortName: "Item list clicks",
    longName: "Item-list click events",
    description:
      "The number of times users clicked an item when it appeared in a list. This metric counts the occurrence of the select_item event.",
    icon: "mouse-pointer",
  },
  itemListClickthroughRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Item list CTR",
    longName: "Item list clickthrough rate",
    description:
      "The number of users who selected a list(s) divided by the number of users who viewed the same list(s).",
    summable: false,
    icon: "hand-pointer",
  },
  itemListViewEvents: {
    formatType: MetricFormat.INTEGER,
    shortName: "Item list views",
    longName: "Item-list view events",
    description:
      "The number of times the item list was viewed. This metric counts the occurrence of the view_item_list event.",
    summable: false,
    icon: "eye",
  },
  itemPromotionClickthroughRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Item promo CTR",
    longName: "Item promotion clickthrough rate",
    description:
      "The number of users who selected a promotion(s) divided by the number of users who viewed the same promotion(s).",
    summable: false,
    icon: "hand-pointer",
  },
  itemViewEvents: {
    formatType: MetricFormat.INTEGER,
    shortName: "Item view events",
    description:
      "The number of times the item details were viewed. The metric counts the occurrence of the view_item event.",
    icon: "eye",
  },
  itemRefundAmount: {
    formatType: MetricFormat.MONETARY,
    shortName: "Item refund amount",
    description:
      "Item refund amount is the total refunded transaction revenue from items only. Item refund amount is the product of price and quantity for the refund event.",
    icon: "money-bill-wave",
  },
  itemRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Item revenue",
    description:
      "The total revenue from purchases minus refunded transaction revenue from items only. Item revenue is the product of its price and quantity. Item revenue excludes tax and shipping values; tax & shipping values are specified at the event and not item level.",
    icon: "money-bill-wave",
  },
  itemsAddedToCart: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items added to cart",
    description:
      "The number of units added to cart for a single item. This metric counts the quantity of items in add_to_cart events.",
    icon: "shopping-cart",
  },
  itemsCheckedOut: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items checked out",
    description:
      "The number of units checked out for a single item. This metric counts the quantity of items in begin_checkout events.",
  },
  itemsClickedInList: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items clicked (list)",
    longName: "Items clicked in list",
    description:
      "The number of units clicked in list for a single item. This metric counts the quantity of items in select_item events.",
    icon: "mouse-pointer",
  },
  itemsClickedInPromotion: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items click. (promo)",
    longName: "Items clicked in promotion",
    description:
      "The number of units clicked in promotion for a single item. This metric counts the quantity of items in select_promotion events.",
    icon: "mouse-pointer",
  },
  itemsPurchased: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items purchased",
    description:
      "The number of units for a single item included in purchase events. This metric counts the quantity of items in purchase events.",
  },
  itemsViewed: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items viewed",
    description:
      "The number of units viewed for a single item. This metric counts the quantity of items in view_item events.",
    icon: "eye",
  },
  itemsViewedInList: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items viewed in list",
    description:
      "The number of units viewed in list for a single item. This metric counts the quantity of items in view_item_list events.",
    icon: "eye",
  },
  itemsViewedInPromotion: {
    formatType: MetricFormat.INTEGER,
    shortName: "Items viewed (promo)",
    longName: "Items viewed in promotion",
    description:
      "The number of units viewed in promotion for a single item. This metric counts the quantity of items in view_promotion events.",
    icon: "eye",
  },
  organicGoogleSearchAveragePosition: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Organic avg. pos.",
    longName: "Organic Google Search average position",
    description:
      "The average ranking of your website URLs for the query reported from Search Console. For example, if your site's URL appears at position 3 for one query and position 7 for another query, the average position would be 5 (3+7/2). This metric requires an active Search Console link.",
    summable: false,
  },
  organicGoogleSearchClickthroughRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Organic search CTR",
    longName: "Organic Google Search clickthrough rate",
    description:
      "The organic Google Search click through rate reported from Search Console. Click through rate is clicks per impression. This metric requires an active Search Console link.",
    summable: false,
    icon: "hand-pointer",
  },
  organicGoogleSearchClicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Organic clicks",
    longName: "Organic Google Search clicks",
    description:
      "The number of organic Google Search clicks reported from Search Console. This metric requires an active Search Console link.",
    icon: "mouse-pointer",
  },
  organicGoogleSearchImpressions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Organic search impr.",
    longName: "Organic Google Search impressions",
    description:
      "The number of organic Google Search impressions reported from Search Console. This metric requires an active Search Console link.",
  },
  promotionClicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Promotion clicks",
    description:
      "The number of times an item promotion was clicked. This metric counts the occurrence of the select_promotion event.",
    icon: "mouse-pointer",
  },
  promotionViews: {
    formatType: MetricFormat.INTEGER,
    shortName: "Promotion views",
    description:
      "The number of times an item promotion was viewed. This metric counts the occurrence of the view_promotion event.",
    icon: "eye",
  },
  publisherAdClicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Publisher ad clicks",
    description: "The number of ad_click events.",
    icon: "mouse-pointer",
  },
  publisherAdImpressions: {
    formatType: MetricFormat.INTEGER,
    shortName: "Publisher ad impr.",
    longName: "Publisher ad impressions",
    description: "The number of ad_impression events.",
  },
  purchaseToViewRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Purch.-to-view rate",
    longName: "Purchase-to-view rate",
    description:
      "The number of users who purchased a product(s) divided by the number of users who viewed the same product(s).",
    summable: false,
  },
  purchaserRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Purchaser rate",
    description:
      "The percentage of active users who made 1 or more purchase transactions.",
    summable: false,
  },
  refundAmount: {
    formatType: MetricFormat.MONETARY,
    shortName: "Refund amount",
    description:
      "The total refunded transaction revenues. Refund amount sums refunded revenue for the refund and app_store_refund events.",
    icon: "money-bill-wave",
  },
  scrolledUsers: {
    formatType: MetricFormat.INTEGER,
    shortName: "Scrolled users",
    description:
      "The number of unique users who scrolled down at least 90% of the page.",
    summable: false,
    icon: "users",
  },
  sessionsPerUser: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Sessions per user",
    description:
      "The average number of sessions per user (Sessions divided by Active Users).",
    summable: false,
    icon: "hourglass-half",
  },
  shippingAmount: {
    formatType: MetricFormat.MONETARY,
    shortName: "Shipping amount",
    description:
      "Shipping amount associated with a transaction. Populated by the shipping event parameter.",
  },
  taxAmount: {
    formatType: MetricFormat.MONETARY,
    shortName: "Tax amount",
    description:
      "Tax amount associated with a transaction. Populated by the tax event parameter.",
  },
  totalAdRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Total ad revenue",
    description:
      "The total advertising revenue from both Admob and third-party sources.",
    icon: "money-bill-wave",
  },
  totalPurchasers: {
    formatType: MetricFormat.INTEGER,
    shortName: "Total purchasers",
    description:
      "The number of users that logged purchase events for the time period selected.",
  },
  totalRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Total revenue",
    description:
      "The sum of revenue from purchases, subscriptions, and advertising (Purchase revenue plus Subscription revenue plus Ad revenue) minus refunded transaction revenue.",
    icon: "money-bill-wave",
  },
  transactionsPerPurchaser: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Trans./purchaser",
    longName: "Transactions per purchaser",
    description: "The average number of transactions per purchaser.",
    summable: false,
  },
  userEngagement: {
    formatType: MetricFormat.DURATION,
    shortName: "User engagement",
    description:
      "The total amount of time your website or app was in the foreground of users' devices.",
    summable: false,
    icon: "heartbeat-solid",
  },
  userConversionRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "User conversion rate",
    description: "The percentage of users who triggered any conversion.",
    summable: false,
  },
  wauPerMau: {
    formatType: MetricFormat.RELATIVE,
    shortName: "WAU / MAU",
    description:
      "The rolling percent of 30-day active users who are also 7-day active users.",
    summable: false,
  },
  totalVideoWatchTime: {
    formatType: MetricFormat.DURATION,
    shortName: "Total video time",
    longName: "Total video watch time",
    description: "The total duration the video was watched per play.",
    icon: "video",
    summable: false,
  },
  outboundClicks: {
    formatType: MetricFormat.INTEGER,
    shortName: "Clicks (Outbound)",
    icon: "mouse-pointer",
    description:
      "The number of clicks on links that lead to destinations outside Meta technologies.",
  },
  videoViews15s: {
    formatType: MetricFormat.INTEGER,
    shortName: "15s video views",
    longName: "15-second video views",
    description:
      "The number of times the video was watched for at least 15 seconds, or to 97% completion for shorter videos. For TikTok, this counts engaged views of 15 seconds or more (views where the user actively watched rather than passively scrolled).",
    icon: "video",
  },
  videoViews6s: {
    formatType: MetricFormat.INTEGER,
    shortName: "TikTok 6s views",
    longName: "TikTok 6-second video views",
    description:
      "TikTok specific. The number of times the video was watched for at least 6 seconds. This is the standard (non-engaged) 6-second video view metric.",
    icon: "video",
  },
  landingPageViews: {
    formatType: MetricFormat.INTEGER,
    shortName: "Landing page views",
    description:
      "The number of times your landing page loaded after a user clicked on your ad.",
    icon: "mouse-pointer",
  },
  storyOpens: {
    formatType: MetricFormat.INTEGER,
    shortName: "Snapchat Story Opens",
    description:
      "The number of times a Snapchatter taps on the Story Ad in the Discover Feed.",
    icon: "video",
  },
  costPerVideoViews15s: {
    formatType: MetricFormat.MONETARY,
    shortName: "Cost/15s video view",
    longName: "Cost per 15-second video view",
    description:
      "Calculated as total cost divided by 15-second video views. Note that this will include cost for ads not eligible for video views.",
    icon: "coins",
    summable: false,
    invertedPositive: true,
  },
  costPerVideoViews6s: {
    formatType: MetricFormat.MONETARY,
    shortName: "Cost/6s video view",
    longName: "Cost per 6-second video view",
    description:
      "TikTok specific. Calculated as total cost divided by 6-second video views. Note that this will include cost for ads not eligible for video views.",
    icon: "coins",
    summable: false,
    invertedPositive: true,
  },
  absoluteTopImpressionPercentage: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Impr. (Abs. Top) %",
    icon: "broadcast-tower",
    description:
      "Google Ads specific. Search absolute top impression rate is the percent of your impressions that are shown as the very first ad among top ads, adjacent to the top organic search results.",
    summable: false,
    aggregatable: false,
  },
  topImpressionPercentage: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Impr. (Top) %",
    icon: "broadcast-tower",
    description:
      "Google Ads specific. Search top impression rate is the percent of your impressions that are shown anywhere adjacent to the top organic search results. Top ads are generally above the top organic results, although they may show below the top organic results on certain queries.",
    summable: false,
    aggregatable: false,
  },
  searchImpressionShare: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Search impr. share",
    icon: "broadcast-tower",
    description:
      "Google Ads specific. Search impression share is the impressions you’ve received on Google search sites divided by the estimated number of impressions you were eligible to receive. Eligibility is based on your current ads' targeting settings, approval statuses, bids, and quality. Impression share data is updated daily. This metric does not include data from Search Partners.",
    summable: false,
    aggregatable: false,
  },
  avgFrequencyV2: {
    formatType: MetricFormat.DECIMAL,
    shortName: "AvgFrequencyV2",
    icon: "signal-stream",
    description:
      "Adform specific. This metric shows the average number of times a visitor is exposed to an ad per selected level and time interval. Calculated as Tracked Ads minus Non-Addressable Tracked Ads, divided by Unique Reach. The calculation also includes first-party IDs and ID Fusion relations, if ID Fusion reporting is enabled.",
    summable: false,
    aggregatable: false,
  },
  uniqueReach: {
    formatType: MetricFormat.DECIMAL,
    shortName: "Unique Reach",
    icon: "broadcast-tower",
    description:
      "Adform specific. This metric shows the number of unique visitors per selected level and time interval. Unique reach is calculated from all available identifiers: third-party IDs, first-party IDs, and ID Fusion relations (if ID Fusion reporting is enabled).",
    summable: false,
    aggregatable: false,
  },
  uniqueImpressionsPercent: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Impr. Unique % (l.)",
    longName: "Impressions Unique (%) (Legacy)",
    icon: "broadcast-tower",
    description:
      "Adform specific. (This is a legacy metric. Please use the new Unique Reach (%) metric instead. The new metric also includes first-party IDs and ID Fusion relations in its calculations, if ID Fusion reporting is enabled.) This metric shows the percentage of unique impressions in relation to all registered impressions.",
    summable: false,
    aggregatable: false,
  },
  uniqueReachPercent: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Unique Reach %",
    longName: "Unique Reach (Tracked Ads, %)",
    icon: "broadcast-tower",
    description:
      "Adform specific. This metric shows the percentage of campaign-level unique reach in relation to tracked ads. Calculated as Unique Reach divided by the difference between Tracked Ads and Non-addressable Tracked Ads, multiplied by 100. The calculation also includes first-party IDs and ID Fusion relations, if ID Fusion reporting is enabled.",
    summable: false,
    aggregatable: false,
  },
  uniqueClicksPercent: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Clicks Unique (%)",
    icon: "broadcast-tower",
    description:
      "Adform specific. This metric shows the percentage of unique clicks in relation to all registered clicks.",
    summable: false,
    aggregatable: false,
  },
  viewability: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Viewability Rate (%)",
    icon: "broadcast-tower",
    description:
      "This metric shows the part of measured impressions that were actually in the users' screens, calculated as the percentage of viewable impressions in relation to measurable impressions.",
    summable: false,
    aggregatable: false,
  },
  searchRankLostImpressionShare: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Lost IS (rank)",
    longName: "Search lost IS (rank)",
    icon: "broadcast-tower",
    description:
      "Google Ads specific. Search lost impression share (rank) estimates how often your ad didn't show on Google search sites due to poor Ad Rank.",
    summable: false,
    aggregatable: false,
  },
  searchBudgetLostImpressionShare: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Lost IS (budget)",
    longName: "Search lost IS (budget)",
    icon: "broadcast-tower",
    description:
      "Google Ads specific. Search lost impression share (budget) estimates how often your ad didn't show on Google search sites due to low budget.",
    summable: false,
    aggregatable: false,
  },
  historicalQualityScore: {
    formatType: MetricFormat.INTEGER,
    shortName: "Quality Score (h.)",
    longName: "Quality Score (historical)",
    icon: "star",
    description:
      "Google Ads specific. Historical Quality Score shows the last known Quality Score for the reporting period. Quality Score is an estimate of how relevant your ads, keywords, and landing pages are to a person who sees your ad. Scored from 1-10, with 10 being the best. Only available at keyword level.",
    summable: false,
    aggregatable: false,
  },
  visibilityShare: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Visibility",
    longName: "AI share of voice",
    icon: "fyr-ai",
    description:
      "How often a brand is mentioned in AI-assistant answers, as a share of the answers analysed. RELATIVE format renders the 0..1 ratio as a percentage.",
    summable: false,
    aggregatable: false,
  },
  crmOrders: {
    formatType: MetricFormat.INTEGER,
    shortName: "Orders",
    longName: "CRM orders",
    icon: "shopping-cart",
  },
  crmRevenue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Revenue",
    longName: "CRM revenue",
    icon: "money-bill-wave",
  },
  crmAvgCartValue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Avg. cart value",
    longName: "Average cart value",
    summable: false,
    icon: "shopping-cart",
  },
  crmReturnRate: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Return rate",
    summable: false,
    invertedPositive: true,
    icon: "arrow-right-arrow-left",
  },
  crmLoyaltyMembers: {
    formatType: MetricFormat.INTEGER,
    shortName: "New members",
    longName: "New loyalty members",
    icon: "users",
  },
  crmMemberShare: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Member share",
    longName: "Loyalty member share",
    summable: false,
    icon: "chart-pie",
  },
  crmClickCollectShare: {
    formatType: MetricFormat.RELATIVE,
    shortName: "Click & Collect",
    longName: "Click & Collect share",
    summable: false,
  },
  crmReturnsValue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Return value",
    longName: "Return value",
  },
  crmPipelineValue: {
    formatType: MetricFormat.MONETARY,
    shortName: "Pipeline value",
    longName: "Project pipeline value",
    icon: "money-bill-wave",
  },
} as const satisfies Record<string, MetricDetailWithOptionalAttributes>;

export type MetricKey = keyof typeof _metricDetails | CalculatedMetricKey;
export type AllMetrics = Record<MetricKey, number | string | undefined | null>;
export type StaticMetricKey = keyof typeof _metricDetails;

export type WebsiteAnalyticsMetric = Analytics4Metric | MatomoMetric;

export const metricDetails = Object.fromEntries(
  Object.entries(_metricDetails).map(
    ([key, details]): [MetricKey, MetricDetail] => [
      key as MetricKey,
      {
        // Fill out defaults for the optional attributes.
        longName: details.shortName,
        summable: true,
        aggregatable: true,
        invertedPositive: false,
        invertedDirection: false,
        description: "",
        ...details,
        key: key as MetricKey,
      },
    ],
  ),
) as Record<MetricKey, MetricDetail>;
export const MetricKeys = Object.keys(metricDetails) as MetricKey[];

export function getMetricDetail(
  keys: MetricKey[] | undefined,
  includeKey?: boolean,
): MetricDetail[] {
  if (keys) {
    return keys.map((key) => {
      if (includeKey) {
        return { ...metricDetails[key], key: key };
      }
      return metricDetails[key];
    });
  }
  return [];
}

// Which denominator the GA4 rate metrics use when a dimension filter is applied.
// "filtered" is GA4's own ratio, whose denominator only covers the filtered rows;
// "total" re-bases it on the unfiltered total. Mirrors ConversionRateDenominator
// in resources/website_performance/views/utils.py.
export type ConversionRateDenominator = "filtered" | "total";

export type ComparisonKey<T extends string> = `comparison${Capitalize<T>}`;

export function comparisonKey<T extends string>(key: T): ComparisonKey<T> {
  return `comparison${upperFirst(key)}`;
}

export function formatNumberByMetricFormat({
  type,
  value,
  organization,
  compact,
  extraCompact = false,
  nullCase = "-",
}: {
  type: MetricFormat;
  value: string | number | null | undefined;
  organization: Pick<Organization, "preferredCurrency"> | null | undefined;
  compact?: boolean;
  extraCompact?: boolean;
  nullCase?: string;
}): string {
  if (value === null) return nullCase;
  if (typeof value === "string") return value.toUpperCase(); // non-object STATE type metrics
  if (type === MetricFormat.INTEGER) {
    return compact
      ? formatNumberCompact(extraCompact, value)
      : formatNumber(value, { maximumFractionDigits: 0 });
  } else if (type === MetricFormat.MONETARY) {
    return organization === null
      ? compact
        ? formatNumberCompact(extraCompact, value)
        : formatNumber(value, { maximumFractionDigits: 0 })
      : compact
        ? formatCurrencyCompact(
            extraCompact,
            value,
            organization?.preferredCurrency,
          )
        : formatCurrency(value, organization?.preferredCurrency);
  } else if (type === MetricFormat.DECIMAL) {
    return compact
      ? formatNumberCompact(extraCompact, value, { maximumFractionDigits: 1 })
      : formatNumber(value, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  } else if (type === MetricFormat.RELATIVE) {
    return compact
      ? formatNumberCompact(extraCompact, value, { style: "percent" })
      : formatNumber(value, { style: "percent", maximumFractionDigits: 2 });
  } else if (type === MetricFormat.DURATION) {
    return formatDuration(value);
  } else if (type === MetricFormat.STATE) {
    return value?.toString().toUpperCase() ?? nullCase;
  }

  assertUnreachable(`Unknown metric format: ${type}` as never);
}

export function formatMetric({
  metric,
  value,
  organization,
  compact,
  extraCompact,
  nullCase = "-",
  metricDetail,
}: {
  metric: MetricKey;
  value: string | number | null | undefined;
  organization: Pick<Organization, "preferredCurrency"> | null | undefined;
  compact?: boolean;
  extraCompact?: boolean;
  nullCase?: string;
  metricDetail?: DynamicMetricDetails;
}): string {
  if (value === null) return nullCase;
  if (metric === "qualityScore" || metric === "historicalQualityScore") {
    return value ? value + "/10" : nullCase;
  }

  if (
    value === 0 &&
    (metric === "absoluteTopImpressionPercentage" ||
      metric === "topImpressionPercentage" ||
      metric === "searchImpressionShare" ||
      metric === "searchRankLostImpressionShare" ||
      metric === "searchBudgetLostImpressionShare")
  ) {
    return nullCase;
  }

  const type: MetricDetail["formatType"] =
    metricDetail?.[metric]?.formatType ||
    metricDetails[metric]?.formatType ||
    MetricFormat.INTEGER;

  return formatNumberByMetricFormat({
    type,
    value,
    organization,
    compact,
    extraCompact,
    nullCase,
  });
}

export interface InjectedMetricSet<T extends MetricKey = MetricKey> {
  name: string;
  metrics: T[];
}

export enum FilterConditionFieldNames {
  EXACT = "exact",
  BELOW = "below",
  MAX = "max",
  MIN = "min",
  ABOVE = "above",
  EXCLUDE = "exclude",
}

export const FilterConditionLookup: Record<FilterConditionFieldNames, string> =
  {
    [FilterConditionFieldNames.EXACT]: "equal to",
    [FilterConditionFieldNames.BELOW]: "less than",
    [FilterConditionFieldNames.MAX]: "less than or equal to",
    [FilterConditionFieldNames.MIN]: "greater than or equal to",
    [FilterConditionFieldNames.ABOVE]: "greater than",
    [FilterConditionFieldNames.EXCLUDE]: "not equal to",
  };

export interface MetricFilterSet {
  metric: MetricKey | undefined | "";
  relationship: FilterConditionFieldNames | undefined;
  value: number;
}

export const MARKETING_METRIC_GROUPS: Record<string, readonly MetricKey[]> = {
  Core: [
    "impressions",
    "clicks",
    "clickthroughRate",
    "costPerClick",
    "costPerMilli",
    "totalClicks",
    "landingPageViews",
    "cost",
  ],
  "Conversions & value": [
    "conversions",
    "costPerConversion",
    "value",
    "returnOnAdSpend",
    "returnOnAdSpendRatio",
    "conversionsByConvDate",
    "valueByConvDate",
    "returnOnAdSpendByConvDate",
    "returnOnAdSpendRatioByConvDate",
  ],
  Engagement: [
    "shares",
    "comments",
    "engagement",
    "reach",
    "frequency",
    "viewability",
  ],
  Video: [
    "initialVideoViews",
    "videoViews25Pct",
    "videoViews50Pct",
    "videoViews75Pct",
    "videoViews100Pct",
    "averageVideoWatchTime",
    "totalVideoWatchTime",
    "costPerInitialVideoView",
    "videoViews15s",
    "costPerVideoViews15s",
  ],
  "Google Ads": [
    "youtubeViews",
    "youtubeViewRate",
    "absoluteTopImpressionPercentage",
    "topImpressionPercentage",
    "searchImpressionShare",
    "searchRankLostImpressionShare",
    "searchBudgetLostImpressionShare",
    "historicalQualityScore",
  ],
  Meta: ["outboundClicks", "metaThruplays", "costPerMetaThruplay"],
  TikTok: ["videoViews6s", "costPerVideoViews6s"],
  Snapchat: ["storyOpens"],
  Adform: ["uniqueReachPercent", "uniqueClicksPercent"],
};

/**
 * Maps metrics that are only available from specific channels to the set of
 * channels that provide them. Metrics NOT listed here are considered universal
 * (available from all marketing channels). A metric should be shown if at
 * least one selected resource belongs to a channel in its set.
 */
// Every story metric across all channels, in card display order. Story cards
// iterate THIS list (filtered per channel via isStoryMetricSupportedForChannel),
// so a metric absent here never renders for ANY channel. Kept here (metrics.ts,
// the lower-level module) so it and SUPPORTED_STORY_METRICS_BY_CHANNEL share one
// definition; social_media.ts re-exports it as `socialMediaStoryMetrics` for its
// literal type.
export const ALL_STORY_METRICS = [
  "reach",
  "impressions",
  "linkClicks",
  "storyReplies",
  "storyInteractions",
  "reactions",
  "shares",
  "storyProfileVisits",
  "storyFollows",
  "storyTapsForward",
  "storyTapsBack",
  "storyExits",
  "storySwipeForward",
] as const satisfies MetricKey[];

// Instagram has no standalone story reactions metric — Meta folds IG story
// reactions into total_interactions; reactions is Facebook-only.
const INSTAGRAM_STORY_METRICS: readonly MetricKey[] = ALL_STORY_METRICS.filter(
  (metric) => metric !== "reactions",
);

// Which story metrics each channel actually supplies. Facebook's story insights
// give only reach/impressions/shares/reactions/replies/interactions (no
// navigation, follows, profile_visits or link_clicks). SINGLE SOURCE OF TRUTH
// for story-metric-per-channel support: the story entries in
// METRIC_CHANNEL_AVAILABILITY below are derived from this by inversion, and
// social_media.ts re-exports it for isStoryMetricSupportedForChannel /
// STORY_CAPABLE_CHANNELS — so the picker, the story card and the sort options
// can't disagree about which story metrics exist for a channel.
export const SUPPORTED_STORY_METRICS_BY_CHANNEL: Partial<
  Record<ResourceChannel, readonly MetricKey[]>
> = {
  [ResourceChannel.INSTAGRAM_ORGANIC]: INSTAGRAM_STORY_METRICS,
  [ResourceChannel.FACEBOOK_ORGANIC]: [
    "reach",
    "impressions",
    "shares",
    "storyReplies",
    "storyInteractions",
    "reactions",
  ],
};

// Story metric → channels that supply it (inverse of the map above), so the
// story entries in METRIC_CHANNEL_AVAILABILITY don't need hand-maintaining.
// Scoped to the story-only keys (reach/impressions/shares/linkClicks have their
// own broader channel lists in METRIC_CHANNEL_AVAILABILITY and are set there;
// reactions is universal and deliberately has no entry at all).
const STORY_ONLY_METRIC_KEYS: readonly MetricKey[] = [
  "storyReplies",
  "storyInteractions",
  "storyProfileVisits",
  "storyFollows",
  "storyTapsForward",
  "storyTapsBack",
  "storyExits",
  "storySwipeForward",
];

function storyMetricAvailability(): Partial<
  Record<MetricKey, readonly ResourceChannel[]>
> {
  const availability: Partial<Record<MetricKey, ResourceChannel[]>> = {};
  for (const [channel, metrics] of Object.entries(
    SUPPORTED_STORY_METRICS_BY_CHANNEL,
  )) {
    for (const metric of metrics ?? []) {
      if (!STORY_ONLY_METRIC_KEYS.includes(metric)) continue;
      (availability[metric] ??= []).push(channel as ResourceChannel);
    }
  }
  return availability;
}

export const METRIC_CHANNEL_AVAILABILITY: Partial<
  Record<MetricKey, readonly ResourceChannel[]>
> = {
  // Google Ads only
  youtubeViews: [ResourceChannel.GOOGLE_ADS],
  youtubeViewRate: [ResourceChannel.GOOGLE_ADS],
  absoluteTopImpressionPercentage: [ResourceChannel.GOOGLE_ADS],
  topImpressionPercentage: [ResourceChannel.GOOGLE_ADS],
  searchImpressionShare: [ResourceChannel.GOOGLE_ADS],
  searchRankLostImpressionShare: [ResourceChannel.GOOGLE_ADS],
  searchBudgetLostImpressionShare: [ResourceChannel.GOOGLE_ADS],
  historicalQualityScore: [ResourceChannel.GOOGLE_ADS],

  // Meta (Facebook Ads) only
  outboundClicks: [ResourceChannel.FACEBOOK_ADS],
  metaThruplays: [ResourceChannel.FACEBOOK_ADS],
  costPerMetaThruplay: [ResourceChannel.FACEBOOK_ADS],

  // TikTok only
  videoViews6s: [ResourceChannel.TIK_TOK_ADS],
  costPerVideoViews6s: [ResourceChannel.TIK_TOK_ADS],

  // Snapchat only
  storyOpens: [ResourceChannel.SNAPCHAT_ADS],

  // Adform only
  uniqueReachPercent: [ResourceChannel.ADFORM],
  uniqueClicksPercent: [ResourceChannel.ADFORM],

  // Snapchat + TikTok
  landingPageViews: [ResourceChannel.SNAPCHAT_ADS, ResourceChannel.TIK_TOK_ADS],
  videoViews15s: [ResourceChannel.SNAPCHAT_ADS, ResourceChannel.TIK_TOK_ADS],
  costPerVideoViews15s: [
    ResourceChannel.SNAPCHAT_ADS,
    ResourceChannel.TIK_TOK_ADS,
  ],

  // Engagement metrics (social-oriented marketing channels)
  shares: [
    ResourceChannel.FACEBOOK_ADS,
    ResourceChannel.LINKED_IN_ADS,
    ResourceChannel.SNAPCHAT_ADS,
    ResourceChannel.TIK_TOK_ADS,
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.INSTAGRAM_ORGANIC,
    ResourceChannel.LINKED_IN_ORGANIC,
    ResourceChannel.TIK_TOK_ORGANIC,
    ResourceChannel.YOUTUBE,
  ],

  // Organic social post metrics added with the wanted-metrics work
  saves: [ResourceChannel.INSTAGRAM_ORGANIC],
  linkClicks: [ResourceChannel.FACEBOOK_ORGANIC],
  videoWatchTime: [ResourceChannel.TIK_TOK_ORGANIC],

  // Story-only metrics are derived from SUPPORTED_STORY_METRICS_BY_CHANNEL
  // (inverted) so their channel scoping lives in exactly one place — stories
  // exist only on IG/FB, and FB has no navigation breakdown, all encoded there.
  ...storyMetricAvailability(),
  comments: [
    ResourceChannel.FACEBOOK_ADS,
    ResourceChannel.LINKED_IN_ADS,
    ResourceChannel.TIK_TOK_ADS,
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.INSTAGRAM_ORGANIC,
    ResourceChannel.LINKED_IN_ORGANIC,
    ResourceChannel.TIK_TOK_ORGANIC,
    ResourceChannel.YOUTUBE,
  ],
  engagement: [
    ResourceChannel.FACEBOOK_ADS,
    ResourceChannel.LINKED_IN_ADS,
    ResourceChannel.SNAPCHAT_ADS,
    ResourceChannel.TIK_TOK_ADS,
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.INSTAGRAM_ORGANIC,
    ResourceChannel.LINKED_IN_ORGANIC,
    ResourceChannel.TIK_TOK_ORGANIC,
    ResourceChannel.YOUTUBE,
  ],

  // NAGR metrics (reach, frequency, viewability) + organic social post reach
  reach: [
    ResourceChannel.FACEBOOK_ADS,
    ResourceChannel.SNAPCHAT_ADS,
    ResourceChannel.TIK_TOK_ADS,
    ResourceChannel.LINKED_IN_ADS,
    ResourceChannel.ADFORM,
    ResourceChannel.DV_360,
    ResourceChannel.C_ALT,
    ResourceChannel.FACEBOOK_ORGANIC,
    ResourceChannel.INSTAGRAM_ORGANIC,
    ResourceChannel.TIK_TOK_ORGANIC,
  ],
  frequency: [
    ResourceChannel.FACEBOOK_ADS,
    ResourceChannel.SNAPCHAT_ADS,
    ResourceChannel.TIK_TOK_ADS,
    ResourceChannel.LINKED_IN_ADS,
    ResourceChannel.ADFORM,
    ResourceChannel.DV_360,
    ResourceChannel.C_ALT,
  ],
  viewability: [
    ResourceChannel.ADFORM,
    ResourceChannel.DV_360,
    ResourceChannel.C_ALT,
  ],
};
